import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, MapPin, Phone, Pencil, Trash2, CheckCircle2, CalendarCheck } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { useQuick } from "../components/Layout";
import { PageHeader, Section, StatusBadge, Empty } from "../components/common";
import { label } from "../lib/constants";
import { dateStr, fmtDay, fmtTime, mapsLink, telLink, gcalLink } from "../lib/helpers";

export default function CalendarPage() {
  const { t, lang } = useLang();
  const { isStaff } = useAuth();
  const quick = useQuick();
  const [day, setDay] = useState(new Date());
  const [month, setMonth] = useState(new Date());
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    const s = new Date(month.getFullYear(), month.getMonth() - 1, 1);
    const e = new Date(month.getFullYear(), month.getMonth() + 2, 0);
    api.get("/appointments", { params: { start: dateStr(s), end: dateStr(e) } }).then((r) => setItems(r.data));
  }, [month]);
  useEffect(() => { load(); }, [load]);
  const sel = dateStr(day);
  const dayItems = items.filter((a) => a.date === sel);
  const upcoming = items.filter((a) => a.date > sel && a.status !== "cancelled").slice(0, 10);
  const marked = items.filter((a) => a.status !== "cancelled").map((a) => new Date(`${a.date}T12:00:00`));
  const setStatus = async (a, status) => {
    try { await api.patch(`/appointments/${a.id}/status`, { status }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (a) => {
    if (!window.confirm(t("Delete appointment?", "¿Eliminar cita?"))) return;
    await api.delete(`/appointments/${a.id}`); load();
  };
  const Card = ({ a }) => (
    <div className="px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`appt-${a.id}`}>
      <div className="flex items-start gap-3">
        <div className="mono text-sm font-semibold text-[#D9531E] w-20 shrink-0">{fmtTime(a.time) || "—"}</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{isStaff ? <Link to={`/customers/${a.customer_id}`}>{a.customer_name}</Link> : a.customer_name}</div>
          <div className="text-sm text-slate-500">{label(a.type, lang)} {a.assigned_name && `· ${a.assigned_name}`}</div>
          <div className="text-sm text-slate-500 truncate">{a.address}</div>
          {a.notes && <div className="text-sm text-slate-700 mt-1">{a.notes}</div>}
        </div>
        <StatusBadge status={a.status} />
      </div>
      <div className="flex flex-wrap gap-2 mt-3 ml-0 sm:ml-[92px]">
        <a className="btn-outline btn-sm" href={mapsLink(a.address)} target="_blank" rel="noreferrer" data-testid={`appt-directions-${a.id}`}><MapPin className="w-4 h-4" /> {t("Directions", "Cómo llegar")}</a>
        {a.customer_phone && <a className="btn-outline btn-sm" href={telLink(a.customer_phone)}><Phone className="w-4 h-4" /></a>}
        {a.status !== "completed" && <button className="btn-outline btn-sm" onClick={() => setStatus(a, "completed")} data-testid={`appt-complete-${a.id}`}><CheckCircle2 className="w-4 h-4" /> {t("Done", "Hecho")}</button>}
        <a className="btn-outline btn-sm" href={gcalLink(a)} target="_blank" rel="noreferrer" data-testid={`appt-gcal-${a.id}`}><CalendarCheck className="w-4 h-4" /> Google Calendar</a>
        {isStaff && <button className="btn-outline btn-sm" onClick={() => quick.openAppt(a, load)} data-testid={`appt-edit-${a.id}`}><Pencil className="w-4 h-4" /></button>}
        {isStaff && <button className="btn-danger btn-sm" onClick={() => del(a)} data-testid={`appt-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
  return (
    <>
      <PageHeader title={isStaff ? t("Calendar", "Calendario") : t("My Schedule", "Mi agenda")}
        actions={isStaff && <button className="btn-primary" onClick={() => quick.openAppt({ date: sel }, load)} data-testid="calendar-new-appt-btn"><CalendarPlus className="w-5 h-5" /> {t("New Appointment", "Nueva cita")}</button>} />
      <div className="grid lg:grid-cols-[auto_1fr] gap-4 items-start">
        <div className="card p-2 flex justify-center" data-testid="calendar-picker">
          <Calendar mode="single" selected={day} onSelect={(d) => d && setDay(d)} month={month} onMonthChange={setMonth}
            modifiers={{ booked: marked }} modifiersClassNames={{ booked: "font-extrabold text-[#D9531E] underline" }} />
        </div>
        <div className="grid gap-4">
          <Section title={fmtDay(sel, lang)} count={dayItems.length} testid="calendar-day-list">
            {dayItems.length ? dayItems.map((a) => <Card key={a.id} a={a} />) : <Empty text={t("No appointments this day", "Sin citas este día")} />}
          </Section>
          <Section title={t("Next appointments", "Próximas citas")} testid="calendar-upcoming-list">
            {upcoming.length ? upcoming.map((a) => (
              <button key={a.id} className="row-link w-full text-left" onClick={() => setDay(new Date(`${a.date}T12:00:00`))}>
                <span className="mono text-sm w-28 shrink-0">{fmtDay(a.date, lang)}</span>
                <span className="flex-1 truncate font-semibold">{a.customer_name}</span>
                <span className="text-xs text-slate-500">{fmtTime(a.time)}</span>
              </button>
            )) : <Empty text="—" />}
          </Section>
          <p className="text-xs text-slate-500">{t("Google Calendar: use the button on each appointment to add it to your Google Calendar. Automatic two-way sync requires Google credentials (see Settings → Integrations).", "Google Calendar: use el botón en cada cita para agregarla a su Google Calendar. La sincronización automática requiere credenciales de Google (vea Configuración → Integraciones).")}</p>
        </div>
      </div>
    </>
  );
}
